import { isEmpty } from 'lodash';
import React, { useCallback } from 'react';

import {
  getListsForManyCategories,
  getListsForManySubcategories,
  setVodAsset,
  translationPacket
} from 'common/actions';
import { VODCard } from 'common/components/cards';
import { CategoryHorizontalList } from 'common/components/category-horizontal-list';
import { Stripe } from 'common/components/stripe';
import { ViewWithDetails } from 'common/components/view-with-details';
import { ROUTE_VOD } from 'common/config/constants';
import variables from 'common/config/variables';
import {
  CategoriesOptions,
  Category,
  Epg,
  EpgsStore,
  IViewWithDetailsStripeItem
} from 'common/constants/data-types';
import protect from 'common/HoC/protect';
import { usePageNavigation } from 'common/utils/hooks';
import { connect } from 'react-redux';

interface StateProps {
  i18n: typeof translationPacket;
  vod: EpgsStore['vod'];
  categoriesOptions: CategoriesOptions;
}

interface VODProps extends StateProps {
  getListsForManySubcategories: (category: Category, vod?: boolean) => Promise<any>;
  getListsForManyCategories: (vod: boolean) => Promise<void>;
  setVodAsset: (vodAsset: Epg, isFullscreen?: boolean) => void;
}

function VOD({ vod, setVodAsset, categoriesOptions }: VODProps) {
  const navigate = usePageNavigation();

  const onMainCategorySelect = useCallback((category: Category) => {
    navigate(`${ROUTE_VOD}/${category.id}`);
  }, []);

  const onSubcategorySelect = useCallback((mainCategory: Category, category: Category) => {
    navigate(`${ROUTE_VOD}/${mainCategory.id}/${category.id}`);
  }, []);

  const openPlayer = useCallback((asset: IViewWithDetailsStripeItem) => {
    setVodAsset(asset);
  }, []);

  const renderEpgItem = useCallback(
    (item: Epg, index: number) => (
      <VODCard
        key={`archive-card-${item.eventid}`}
        epg={item}
        showTitle={categoriesOptions?.[item.majorId]?.SHOW_CATEGORY_CARD_TITLE}
        testId={`vod-epg-card-${index}`}
      />
    ),
    [categoriesOptions]
  );

  const getFocusKeyOnVodCategory = useCallback((item: Category, index: number) => {
    return `vod-item-${item.id}-${index}`;
  }, []);

  const renderCategoryList = useCallback(() => {
    if (isEmpty(vod.categories) || isEmpty(vod.categoriesEpgs)) {
      return null;
    }
    return Object.values(vod.categories).flatMap((category) => {
      const epgs = (vod.categoriesEpgs[category.id] as Epg[]).filter((e) => !!e.epgs?.length);
      if (isEmpty(epgs)) {
        return [];
      }

      return (
        <Stripe
          key={`vod-category-stripe-${category.id}`}
          items={epgs}
          renderItem={renderEpgItem}
          title={
            <CategoryHorizontalList
              category={category}
              onMainCategorySelect={onMainCategorySelect}
              onSubcategorySelect={onSubcategorySelect}
              getFocusKey={getFocusKeyOnVodCategory}
            />
          }
          onStripeItemPress={openPlayer}
          offsetBottom={variables.STRIPE_OFFSET_BOTTOM_PADDING}
          testId={`vod-category-stripe-${category.id}`}
        />
      );
    });
  }, [vod.categories, vod.categoriesEpgs]);

  const getSelectedAssetData = useCallback((asset: IViewWithDetailsStripeItem) => {
    const firstEpg = asset.epgs?.[0];
    return {
      epg: {
        ...asset,
        vod: true,
        year: asset.year || asset.releaseDate?.split('-')[0],
        duration: asset.duration || firstEpg?.duration || ''
      },
      resolution: firstEpg?.profiles?.[0]?.resolution,
      thumbnailUrl: asset.photos.find((photo) => photo.type === 'cover')?.url
    };
  }, []);

  return (
    <ViewWithDetails
      focusKey={location.pathname}
      saveFocusOnUnmount={false}
      getSelectedAssetData={getSelectedAssetData}
      disableVirtual
    >
      {[renderCategoryList()]}
    </ViewWithDetails>
  );
}

const mapStateToProps = ({ i18n, epgs }: any) => ({
  i18n,
  vod: epgs.vod,
  categoriesOptions: epgs?.vod?.categoriesOptions
});
export default React.memo(
  protect(
    connect(mapStateToProps, {
      setVodAsset,
      getListsForManySubcategories,
      getListsForManyCategories
    })(VOD)
  )
);
